// src/components/ReceiveFlow.jsx
import { useState, useEffect, useCallback } from "react";
import {
  Card, StepLabel, CardTitle, CardDesc,
  Chip, Input, Textarea, Divider,
  ProgressBar, BtnMain,
} from "./UI";
import PaperSelector from "./PaperSelector";
import LetterResult from "./LetterResult";
import {
  ANIMALS, PERSONALITIES, NICKNAMES, PERIODS, GENDERS,
  ANIMAL_EMOJIS, PAPERS,
} from "../utils/constants";
import { buildSystemPrompt, buildReceivePrompt } from "../utils/buildPrompt";
import { generateLetter } from "../utils/api";
import { subjectParticle } from "../utils/particle";

const TOTAL_STEPS = 6;
const DRAFT_KEY = "petmal_draft";

function saveDraft(data) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(data)); } catch {}
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

// ── 편지 작성 연출 ──
// "완료하기" 시점에 편지 생성이 백그라운드에서 시작되고,
// 여기서는 10초(2초 x 5단계) 연출을 보여준 뒤 생성 완료와 동시에 자동으로 편지를 연다.
const GATE_STAGES = [
  "🐾 함께한 추억을 읽어보는 중...",
  "💭 마음속 이야기를 여쭤보는 중...",
  "✏️ 편지지에 꾹꾹 눌러 쓰는 중...",
  "🎀 봉투에 소중히 담는 중...",
  "🕊️ 하늘 우체국에서 배달하는 중...",
];
const GATE_STAGE_MS = 2000;

function WritingGate({ animalName, ready, onComplete }) {
  const [stage, setStage] = useState(0); // 0 ~ GATE_STAGES.length (마지막 = 연출 종료)

  useEffect(() => {
    if (stage >= GATE_STAGES.length) return;
    const t = setTimeout(() => setStage((s) => s + 1), GATE_STAGE_MS);
    return () => clearTimeout(t);
  }, [stage]);

  const stagesDone = stage >= GATE_STAGES.length;

  // 10초 연출이 끝나고 편지도 준비되면 자동으로 열기
  useEffect(() => {
    if (!stagesDone || !ready) return;
    const t = setTimeout(onComplete, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagesDone, ready]);

  const message = stagesDone
    ? "💌 거의 도착했어요..."
    : GATE_STAGES[stage];
  const progress = Math.min(((stage + 1) / (GATE_STAGES.length + 1)) * 100, 95);

  return (
    <div style={{ padding: "24px 4px 8px", animation: "fadeUp .4s ease" }}>
      {/* 헤더 */}
      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <span style={{ fontSize: 56, display: "block", marginBottom: 14, animation: "float 2.5s ease-in-out infinite" }}>
          ✉️
        </span>
        <div style={{
          fontFamily: "'Jua', sans-serif", fontSize: 24, fontWeight: 700,
          color: "#5c3d2e", marginBottom: 8,
        }}>
          {subjectParticle(animalName)} 편지를 쓰고 있어요
        </div>

        {/* 단계별 연출 메시지 */}
        <div style={{
          fontSize: 16, fontWeight: 400, color: "#a08060", lineHeight: 1.8,
          minHeight: 29, animation: "fadeUp .3s ease",
        }} key={message}>
          {message}
        </div>

        {/* 진행 바 */}
        <div style={{
          maxWidth: 260, height: 8, margin: "14px auto 0",
          background: "#f0e4d8", borderRadius: 4, overflow: "hidden",
        }}>
          <div style={{
            width: `${progress}%`, height: "100%",
            background: "linear-gradient(90deg, #c4845a, #d4a070)",
            borderRadius: 4, transition: "width 1.8s ease",
          }} />
        </div>
      </div>
    </div>
  );
}

// ── 확인 모달 ──
function ConfirmModal({ title, message, confirmText, cancelText, onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{
        background: "white", borderRadius: 24, padding: "32px 28px",
        maxWidth: 360, width: "100%",
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        animation: "fadeUp .3s ease",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>🐾</div>
        <div style={{
          fontFamily: "'Jua', sans-serif", fontSize: 18, fontWeight: 700, color: "#5c3d2e",
          marginBottom: 12,
        }}>
          {title}
        </div>
        <div style={{
          fontSize: 16, fontWeight: 400, color: "#b09070", lineHeight: 1.8, marginBottom: 24,
        }}>
          {message}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: 14, borderRadius: 14,
              background: "white", color: "#b09070", fontSize: 16, fontWeight: 700,
              fontFamily: "'Jua', sans-serif",
              border: "1.5px solid #ead6c0", cursor: "pointer",
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, padding: 14, borderRadius: 14,
              background: "linear-gradient(135deg, #c4845a, #d4a070)",
              color: "white", fontSize: 16, fontWeight: 700,
              fontFamily: "'Jua', sans-serif",
              border: "none", cursor: "pointer",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 메인 플로우 ──
export default function ReceiveFlow({ onBack }) {
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("form"); // form | loading | letter | error

  // 폼 상태
  const [gender, setGender] = useState("");
  const [userName, setUserName] = useState("");
  const [nickname, setNickname] = useState("");
  const [customNickname, setCustomNickname] = useState("");
  const [animalType, setAnimalType] = useState("");
  const [customAnimalType, setCustomAnimalType] = useState("");
  const [animalName, setAnimalName] = useState("");
  const [aliveStatus, setAliveStatus] = useState("");
  const [petAge, setPetAge] = useState("");
  const [petAgeUnknown, setPetAgeUnknown] = useState(false);
  const [period, setPeriod] = useState("");
  const [personalities, setPersonalities] = useState([]);
  const [personalityText, setPersonalityText] = useState("");
  const [episodes, setEpisodes] = useState([{ title: "", content: "" }]);
  const [paper, setPaper] = useState("p1");

  // 결과
  const [letter, setLetter] = useState("");
  const [genReady, setGenReady] = useState(false); // 백그라운드 편지 생성 완료 여부

  // 검토(확인) 화면 — 완료하기 누르면 reviewDone=true → 작성 연출(WritingGate) 노출
  const [reviewDone, setReviewDone] = useState(false);
  const [editSection, setEditSection] = useState(null); // 인라인 수정 중인 섹션 키

  // 모달
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  // 실제 동물 타입 (기타면 직접입력 값 사용)
  const effectiveAnimalType = animalType === "기타" ? customAnimalType : animalType;

  // 초기 로드: localStorage 이어서 쓰기
  useEffect(() => {
    const draft = loadDraft();
    if (draft && draft.userName) {
      setPendingDraft(draft);
      setShowResume(true);
    }
  }, []);

  const resumeDraft = () => {
    if (!pendingDraft) return;
    const d = pendingDraft;
    setGender(d.gender || "");
    setUserName(d.userName || "");
    setNickname(d.nickname || "");
    setCustomNickname(d.customNickname || "");
    setAnimalType(d.animalType || "");
    setCustomAnimalType(d.customAnimalType || "");
    setAnimalName(d.animalName || "");
    setAliveStatus(d.aliveStatus || "");
    setPetAge(d.petAge || "");
    setPetAgeUnknown(d.petAgeUnknown || false);
    setPeriod(d.period || "");
    setPersonalities(d.personalities || []);
    setPersonalityText(d.personalityText || "");
    setEpisodes(d.episodes || [{ title: "", content: "" }]);
    setPaper(d.paper || "p1");
    setStep(d.step || 0);
    setShowResume(false);
    setPendingDraft(null);
  };

  const discardDraft = () => {
    clearDraft();
    setShowResume(false);
    setPendingDraft(null);
  };

  // 폼 데이터 자동 저장 (step이 바뀔 때마다)
  const currentDraftData = useCallback(() => ({
    gender, userName, nickname, customNickname,
    animalType, customAnimalType, animalName,
    aliveStatus, petAge, petAgeUnknown, period, personalities, personalityText,
    episodes, paper, step,
  }), [gender, userName, nickname, customNickname, animalType, customAnimalType, animalName, aliveStatus, petAge, petAgeUnknown, period, personalities, personalityText, episodes, paper, step]);

  useEffect(() => {
    // 폼 작성 중 + 검토 화면에서 인라인 수정 중에도 저장 (생성 단계 제외)
    const editing = phase === "form" && (step < 5 || !reviewDone);
    if (editing) {
      saveDraft(currentDraftData());
    }
  }, [step, phase, reviewDone, currentDraftData]);

  const toggleP = (p) =>
    setPersonalities((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );

  const addEp = () => {
    if (episodes.length < 5) setEpisodes([...episodes, { title: "", content: "" }]);
  };

  const updateEp = (i, field, val) => {
    const updated = [...episodes];
    updated[i][field] = val;
    setEpisodes(updated);
  };

  const removeEp = (i) => {
    if (episodes.length > 1) setEpisodes(episodes.filter((_, idx) => idx !== i));
  };

  // 스텝별 유효성 (검토 화면에서도 재사용)
  const stepValid = (s) => {
    if (s === 0) return gender && userName && nickname && (nickname !== "직접입력" || customNickname);
    if (s === 1) {
      const typeOk = animalType && (animalType !== "기타" || customAnimalType.trim());
      return typeOk && animalName && aliveStatus && (aliveStatus === "alive" || period);
    }
    if (s === 2) return personalities.length > 0 || personalityText.trim();
    if (s === 3) return episodes[0].title && episodes[0].content.length >= 20;
    if (s === 4) return true;
    return false;
  };

  const canNext = () => stepValid(step);

  // 검토 화면 — 모든 필수 입력이 유효해야 완료 가능
  const reviewValid = () => [0, 1, 2, 3].every(stepValid);

  // "완료하기" 즉시(작성 연출 진입과 동시에) 백그라운드에서 편지 생성 시작
  useEffect(() => {
    if (!reviewDone || step !== 5) return;

    const system = buildSystemPrompt();
    const prompt = buildReceivePrompt({
      gender, userName, nickname, customNickname,
      animalType: effectiveAnimalType, animalName, aliveStatus,
      petAge: petAgeUnknown ? null : (petAge !== "" ? Number(petAge) : null),
      period,
      personalities: personalityText.trim()
        ? [...personalities, personalityText.trim()]
        : personalities,
      episodes,
    });

    let cancelled = false;
    generateLetter(system, prompt)
      .then((text) => {
        if (!cancelled) {
          setLetter(text);
          setGenReady(true); // AdGate 연출이 끝나면 자동으로 편지가 열림
          clearDraft(); // 편지 생성 성공 시 초안 삭제
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhase("error");
        }
      });

    return () => { cancelled = true; setGenReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewDone]);

  // 뒤로가기
  const handleBack = () => {
    if (phase === "letter") return; // 편지 결과에서는 LetterResult 내에서 처리
    if (phase === "error") {
      setPhase("form");
      setReviewDone(false);
      setStep(4);
      return;
    }
    if (phase !== "form") {
      setPhase("form");
      setReviewDone(false);
      setStep(4);
      return;
    }
    if (step === 5) {
      // 작성 연출 → 검토 화면으로 / 검토 화면 → 편지지 단계로
      if (reviewDone) {
        setReviewDone(false);
        return;
      }
      setEditSection(null);
      setStep(4);
      return;
    }
    if (step > 0) { setStep(step - 1); return; }
    // step 0에서 뒤로가기 → 확인 팝업
    setShowConfirm(true);
  };

  const confirmLeave = () => {
    setShowConfirm(false);
    // 작성한 내용이 있으면 저장됨 (이미 자동저장)
    onBack();
  };

  // 에피소드 수정하기 (편지 결과에서)
  const handleEditEpisodes = () => {
    setPhase("form");
    setReviewDone(false);
    setLetter("");
    setStep(3); // 에피소드 단계로
  };

  // 처음으로 (편지 결과에서)
  const handleGoHome = () => {
    clearDraft();
    onBack();
  };

  // ── 검토(확인) 화면 ──
  // 섹션 래퍼: 보기 모드 ↔ 인라인 수정 모드 토글
  const reviewSection = (key, title, summaryNode, editNode, valid = true) => {
    const open = editSection === key;
    return (
      <div style={{
        background: "white", borderRadius: 20, padding: "18px 20px", marginBottom: 14,
        boxShadow: "0 2px 14px rgba(92,61,46,0.06)",
        border: `1.5px solid ${valid ? "transparent" : "#e8a890"}`,
        transition: "border-color .2s",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontFamily: "'Jua', sans-serif", fontSize: 16, color: "#5c3d2e", display: "flex", alignItems: "center", gap: 6 }}>
            {title}
            {!valid && <span style={{ fontSize: 12, color: "#d18a6a" }}>· 입력 필요</span>}
          </div>
          <button
            onClick={() => setEditSection(open ? null : key)}
            style={{
              flexShrink: 0, padding: "6px 16px", borderRadius: 12,
              border: `1.5px solid ${open ? "#c4845a" : "#ead6c0"}`,
              background: open ? "#c4845a" : "white",
              color: open ? "white" : "#b09070",
              fontSize: 14, fontWeight: 700, fontFamily: "'Jua', sans-serif", cursor: "pointer",
              transition: "all .2s",
            }}
          >
            {open ? "완료" : "수정"}
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          {open ? editNode : summaryNode}
        </div>
      </div>
    );
  };

  const sumText = (children, style = {}) => (
    <div style={{ fontSize: 15, color: "#5c3d2e", lineHeight: 1.7, fontFamily: "'Gowun Batang', serif", ...style }}>
      {children}
    </div>
  );
  const sumSub = (children) => (
    <div style={{ fontSize: 13, color: "#b09070", marginTop: 5, lineHeight: 1.6, fontFamily: "'Gowun Batang', serif" }}>
      {children}
    </div>
  );

  const renderReview = () => {
    const nickDisplay = nickname === "직접입력"
      ? (customNickname || "—")
      : nickname === "이름으로"
        ? `${userName || "이름"} (이름으로)`
        : (nickname || "—");
    const petEmoji = ANIMAL_EMOJIS[animalType] || "🐾";
    const statusText = aliveStatus === "alive"
      ? "🏠 지금도 함께하고 있어요"
      : aliveStatus === "rainbow"
        ? `🌈 하늘에서 기다리고 있어요${(!petAgeUnknown && petAge !== "") ? ` · ${petAge}살에 떠남` : ""}${period ? ` · ${period}` : ""}`
        : "—";
    const persList = [...personalities];
    if (personalityText.trim()) persList.push(personalityText.trim());
    const paperObj = PAPERS.find((p) => p.id === paper) || PAPERS[0];
    const filledEpisodes = episodes.filter((e) => e.title || e.content);
    const valid = reviewValid();

    return (
      <div style={{ animation: "fadeUp .4s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <span style={{ fontSize: 44, display: "block", marginBottom: 10 }}>📋</span>
          <div style={{ fontFamily: "'Jua', sans-serif", fontSize: 24, fontWeight: 700, color: "#5c3d2e", marginBottom: 8 }}>
            입력하신 내용을 확인해주세요
          </div>
          <div style={{ fontSize: 15, fontWeight: 400, color: "#b09070", lineHeight: 1.8 }}>
            고치고 싶은 곳은 '수정'을 눌러 바꿔주세요.<br />
            다 맞으면 아래 '완료하기'를 눌러주세요.
          </div>
        </div>

        {/* 1. 받는 사람 */}
        {reviewSection("recipient", "받는 사람",
          <>
            {sumText(<>{userName || "—"} <span style={{ color: "#b09070", fontSize: 13 }}>· {gender || "—"}</span></>)}
            {sumSub(<>동물 친구가 불러주는 말: {nickDisplay}</>)}
          </>,
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              {GENDERS.map((g) => (
                <Chip key={g} label={g} selected={gender === g} onClick={() => setGender(g)} />
              ))}
            </div>
            <Input placeholder="이름 또는 별명" value={userName} onChange={(e) => setUserName(e.target.value)} />
            <div style={{ fontSize: 14, color: "#b09070", margin: "8px 0 10px" }}>동물 친구가 나를 뭐라고 불렀을 것 같아요?</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              {NICKNAMES.map((n) => (
                <Chip key={n} label={n} selected={nickname === n} onClick={() => setNickname(n)} />
              ))}
            </div>
            {nickname === "직접입력" && (
              <Input placeholder="직접 입력 (예: 집사님)" value={customNickname} onChange={(e) => setCustomNickname(e.target.value)} />
            )}
          </>,
          stepValid(0))}

        {/* 2. 동물 친구 */}
        {reviewSection("pet", "동물 친구",
          <>
            {sumText(<>{petEmoji} {effectiveAnimalType || "—"} <span style={{ color: "#b09070", fontSize: 13 }}>· {animalName || "—"}</span></>)}
            {sumSub(statusText)}
          </>,
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
              {ANIMALS.map((a) => (
                <button
                  key={a.l}
                  onClick={() => setAnimalType(a.l)}
                  style={{
                    padding: "14px 6px", borderRadius: 16,
                    border: `1.5px solid ${animalType === a.l ? "#c4845a" : "#ead6c0"}`,
                    background: animalType === a.l ? "#fff0e8" : "white",
                    cursor: "pointer", textAlign: "center", transition: "all .2s",
                  }}
                >
                  <span style={{ fontSize: 28, display: "block", marginBottom: 4 }}>{a.e}</span>
                  <span style={{ fontSize: 13, color: animalType === a.l ? "#c4845a" : "#b09070" }}>{a.l}</span>
                </button>
              ))}
            </div>
            {animalType === "기타" && (
              <Input
                placeholder="어떤 동물인가요? (예: 이구아나, 페럿)"
                value={customAnimalType}
                onChange={(e) => setCustomAnimalType(e.target.value)}
                style={{ marginBottom: 14 }}
              />
            )}
            <Input placeholder="이름이 뭐예요?" value={animalName} onChange={(e) => setAnimalName(e.target.value)} />
            <Divider />
            <CardDesc style={{ marginBottom: 12 }}>지금 함께하고 있나요?</CardDesc>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              {[
                { id: "alive", e: "🏠", name: "네, 함께해요", sub: "지금도 함께하고 있어요" },
                { id: "rainbow", e: "🌈", name: "하늘에서 기다리고 있어요", sub: "멀리 있지만 마음은 가까워요" },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setAliveStatus(s.id)}
                  style={{
                    flex: 1, padding: 16, borderRadius: 16,
                    border: `1.5px solid ${aliveStatus === s.id ? "#c4845a" : "#ead6c0"}`,
                    background: aliveStatus === s.id ? "#fff0e8" : "white",
                    cursor: "pointer", textAlign: "center", transition: "all .2s",
                  }}
                >
                  <span style={{ fontSize: 28, display: "block", marginBottom: 6 }}>{s.e}</span>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#5c3d2e", fontFamily: "'Jua', sans-serif" }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: aliveStatus === s.id ? "#c4845a" : "#b09070", marginTop: 3 }}>{s.sub}</div>
                </button>
              ))}
            </div>
            {aliveStatus === "rainbow" && (
              <>
                <div style={{ fontSize: 16, fontWeight: 400, color: "#b09070", margin: "4px 0 10px" }}>몇 살에 떠났나요?</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    placeholder="나이 (0~30)"
                    value={petAge}
                    disabled={petAgeUnknown}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || (Number(v) >= 0 && Number(v) <= 30)) setPetAge(v);
                    }}
                    style={{ flex: 1, opacity: petAgeUnknown ? 0.4 : 1 }}
                  />
                  <label style={{
                    display: "flex", alignItems: "center", gap: 6,
                    fontSize: 14, color: "#b09070", cursor: "pointer", whiteSpace: "nowrap",
                  }}>
                    <input
                      type="checkbox"
                      checked={petAgeUnknown}
                      onChange={(e) => {
                        setPetAgeUnknown(e.target.checked);
                        if (e.target.checked) setPetAge("");
                      }}
                      style={{ width: 18, height: 18, accentColor: "#c4845a" }}
                    />
                    잘 모르겠어요
                  </label>
                </div>
                <div style={{ fontSize: 16, fontWeight: 400, color: "#b09070", margin: "4px 0 10px" }}>떠나보낸 지 얼마나 됐나요?</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {PERIODS.map((p) => (
                    <Chip key={p} label={p} selected={period === p} onClick={() => setPeriod(p)} />
                  ))}
                </div>
              </>
            )}
          </>,
          stepValid(1))}

        {/* 3. 성격 */}
        {reviewSection("personality", "성격",
          persList.length
            ? sumText(persList.join(" · "))
            : sumSub("입력된 성격이 없어요"),
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              {PERSONALITIES.map((p) => (
                <Chip key={p} label={p} selected={personalities.includes(p)} onClick={() => toggleP(p)} />
              ))}
            </div>
            <div style={{ fontSize: 14, color: "#b09070", marginBottom: 8 }}>위에 없는 성격이 있다면 직접 적어주세요</div>
            <Input
              placeholder="예: 잠이 많음, 간식 앞에서만 말을 잘 들음"
              value={personalityText}
              onChange={(e) => setPersonalityText(e.target.value)}
            />
          </>,
          stepValid(2))}

        {/* 4. 소중한 순간들 */}
        {reviewSection("episodes", "함께한 소중한 순간들",
          filledEpisodes.length
            ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filledEpisodes.map((ep, i) => (
                  <div key={i}>
                    {sumText(<><span style={{ color: "#c4845a", fontSize: 13 }}>{i + 1}. </span>{ep.title || "(제목 없음)"}</>)}
                    {ep.content && sumSub(ep.content.length > 60 ? ep.content.slice(0, 60) + "…" : ep.content)}
                  </div>
                ))}
              </div>
            )
            : sumSub("입력된 에피소드가 없어요"),
          <>
            {episodes.map((ep, i) => (
              <div
                key={i}
                style={{
                  background: "#fdf9f5", border: "1.5px dashed #e4d0bc", borderRadius: 16,
                  padding: 20, marginBottom: 12, position: "relative",
                }}
              >
                <div style={{ fontSize: 12, color: "#c4845a", fontFamily: "'Jua', sans-serif", letterSpacing: 1, marginBottom: 10 }}>
                  에피소드 {i + 1}
                </div>
                {episodes.length > 1 && (
                  <button
                    onClick={() => removeEp(i)}
                    style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", color: "#c8b0a0", cursor: "pointer", fontSize: 16 }}
                  >
                    ✕
                  </button>
                )}
                <Input
                  placeholder="제목 (예: 처음 만난 날)"
                  value={ep.title}
                  onChange={(e) => updateEp(i, "title", e.target.value)}
                />
                <Textarea
                  placeholder={"누가 무엇을 했는지 자유롭게 써주세요. (20자 이상)\n예: 내가 울고 있었는데 무릎에 올라와서 핥아줬다"}
                  value={ep.content}
                  onChange={(e) => updateEp(i, "content", e.target.value)}
                />
                <div style={{
                  fontSize: 11,
                  color: ep.content.length >= 20 ? "#a8b89a" : "#c8b0a0",
                  textAlign: "right", marginTop: -8, marginBottom: 4,
                }}>
                  {ep.content.length}자 {ep.content.length < 20 ? `(${20 - ep.content.length}자 더 써주세요)` : "✓"}
                </div>
              </div>
            ))}
            {episodes.length < 5 && (
              <button
                onClick={addEp}
                style={{
                  width: "100%", padding: 14, borderRadius: 14,
                  border: "1.5px dashed #e4d0bc", background: "none",
                  color: "#b09070", fontSize: 16, fontWeight: 400, cursor: "pointer",
                  fontFamily: "'Gowun Batang', serif",
                }}
              >
                + 에피소드 추가하기
              </button>
            )}
          </>,
          stepValid(3))}

        {/* 5. 편지지 */}
        {reviewSection("paper", "편지지",
          sumText(<>{paperObj.e} {paperObj.n}</>),
          <PaperSelector selected={paper} onChange={setPaper} />,
          true)}

        {/* 완료하기 → 작성 연출 노출 */}
        <BtnMain disabled={!valid} onClick={() => { setEditSection(null); setReviewDone(true); }}>
          완료하기
        </BtnMain>
        {!valid && (
          <div style={{ fontSize: 13, color: "#d18a6a", textAlign: "center", marginTop: 10, lineHeight: 1.6 }}>
            ‘입력 필요’로 표시된 항목을 채워주세요.
          </div>
        )}
      </div>
    );
  };

  // ── 스텝 렌더 ──
  const renderStep = () => {
    if (step === 0) return (
      <Card>
        <StepLabel>STEP 1</StepLabel>
        <CardTitle>먼저 당신을 알려주세요 🌿</CardTitle>
        <CardDesc>편지를 받을 분이 누구인지 알려주세요.</CardDesc>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {GENDERS.map((g) => (
            <Chip key={g} label={g} selected={gender === g} onClick={() => setGender(g)} />
          ))}
        </div>
        <Input
          placeholder="이름 또는 별명"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
        />

        <Divider />
        <CardDesc style={{ marginBottom: 12 }}>동물 친구가 나를 뭐라고 불렀을 것 같아요?</CardDesc>
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {NICKNAMES.map((n) => (
            <Chip key={n} label={n} selected={nickname === n} onClick={() => setNickname(n)} />
          ))}
        </div>
        {nickname === "직접입력" && (
          <Input
            placeholder="직접 입력 (예: 집사님)"
            value={customNickname}
            onChange={(e) => setCustomNickname(e.target.value)}
          />
        )}
      </Card>
    );

    if (step === 1) return (
      <Card>
        <StepLabel>STEP 2</StepLabel>
        <CardTitle>어떤 친구인가요? 🐾</CardTitle>
        <CardDesc>편지를 써줄 동물 친구를 소개해주세요.</CardDesc>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
          {ANIMALS.map((a) => (
            <button
              key={a.l}
              onClick={() => setAnimalType(a.l)}
              style={{
                padding: "14px 6px",
                borderRadius: 16,
                border: `1.5px solid ${animalType === a.l ? "#c4845a" : "#ead6c0"}`,
                background: animalType === a.l ? "#fff0e8" : "white",
                cursor: "pointer",
                textAlign: "center",
                transition: "all .2s",
              }}
            >
              <span style={{ fontSize: 28, display: "block", marginBottom: 4 }}>{a.e}</span>
              <span style={{ fontSize: 13, color: animalType === a.l ? "#c4845a" : "#b09070" }}>{a.l}</span>
            </button>
          ))}
        </div>

        {animalType === "기타" && (
          <Input
            placeholder="어떤 동물인가요? (예: 이구아나, 페럿)"
            value={customAnimalType}
            onChange={(e) => setCustomAnimalType(e.target.value)}
            style={{ marginBottom: 14 }}
          />
        )}

        <Input
          placeholder="이름이 뭐예요?"
          value={animalName}
          onChange={(e) => setAnimalName(e.target.value)}
        />

        <Divider />

        <CardDesc style={{ marginBottom: 12 }}>지금 함께하고 있나요?</CardDesc>
        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          {[
            { id: "alive", e: "🏠", name: "네, 함께해요", sub: "지금도 함께하고 있어요" },
            { id: "rainbow", e: "🌈", name: "하늘에서 기다리고 있어요", sub: "멀리 있지만 마음은 가까워요" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setAliveStatus(s.id)}
              style={{
                flex: 1, padding: 16, borderRadius: 16,
                border: `1.5px solid ${aliveStatus === s.id ? "#c4845a" : "#ead6c0"}`,
                background: aliveStatus === s.id ? "#fff0e8" : "white",
                cursor: "pointer", textAlign: "center", transition: "all .2s",
              }}
            >
              <span style={{ fontSize: 28, display: "block", marginBottom: 6 }}>{s.e}</span>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#5c3d2e", fontFamily: "'Jua', sans-serif" }}>{s.name}</div>
              <div style={{ fontSize: 12, color: aliveStatus === s.id ? "#c4845a" : "#b09070", marginTop: 3 }}>{s.sub}</div>
            </button>
          ))}
        </div>

        {aliveStatus === "rainbow" && (
          <>
            <div style={{ fontSize: 16, fontWeight: 400, color: "#b09070", margin: "4px 0 10px" }}>몇 살에 떠났나요?</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <Input
                type="number"
                min="0"
                max="30"
                placeholder="나이 (0~30)"
                value={petAge}
                disabled={petAgeUnknown}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || (Number(v) >= 0 && Number(v) <= 30)) setPetAge(v);
                }}
                style={{ flex: 1, opacity: petAgeUnknown ? 0.4 : 1 }}
              />
              <label style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 14, color: "#b09070", cursor: "pointer",
                whiteSpace: "nowrap",
              }}>
                <input
                  type="checkbox"
                  checked={petAgeUnknown}
                  onChange={(e) => {
                    setPetAgeUnknown(e.target.checked);
                    if (e.target.checked) setPetAge("");
                  }}
                  style={{ width: 18, height: 18, accentColor: "#c4845a" }}
                />
                잘 모르겠어요
              </label>
            </div>

            <div style={{ fontSize: 16, fontWeight: 400, color: "#b09070", margin: "4px 0 10px" }}>떠나보낸 지 얼마나 됐나요?</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {PERIODS.map((p) => (
                <Chip key={p} label={p} selected={period === p} onClick={() => setPeriod(p)} />
              ))}
            </div>
          </>
        )}
      </Card>
    );

    if (step === 2) return (
      <Card>
        <StepLabel>STEP 3</StepLabel>
        <CardTitle>{animalName || "친구"}는 어떤 성격인가요? 💭</CardTitle>
        <CardDesc>그 친구를 조금 더 알려주시면 편지가 더 깊어져요.</CardDesc>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          {PERSONALITIES.map((p) => (
            <Chip
              key={p}
              label={p}
              selected={personalities.includes(p)}
              onClick={() => toggleP(p)}
            />
          ))}
        </div>
        <CardDesc style={{ marginBottom: 8 }}>위에 없는 성격이 있다면 직접 적어주세요</CardDesc>
        <Input
          placeholder="예: 잠이 많음, 간식 앞에서만 말을 잘 들음"
          value={personalityText}
          onChange={(e) => setPersonalityText(e.target.value)}
        />
      </Card>
    );

    if (step === 3) return (
      <Card>
        <StepLabel>STEP 4</StepLabel>
        <CardTitle>함께했던 소중한 순간들 📖</CardTitle>
        <CardDesc>마음에 남아 있는 기억을 적어주세요. 편지에 고스란히 담아드릴게요.</CardDesc>

        {episodes.map((ep, i) => (
          <div
            key={i}
            style={{
              background: "#fdf9f5",
              border: "1.5px dashed #e4d0bc",
              borderRadius: 16,
              padding: 20,
              marginBottom: 12,
              position: "relative",
            }}
          >
            <div style={{ fontSize: 12, color: "#c4845a", fontFamily: "'Jua', sans-serif", letterSpacing: 1, marginBottom: 10 }}>
              에피소드 {i + 1}
            </div>
            {episodes.length > 1 && (
              <button
                onClick={() => removeEp(i)}
                style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", color: "#c8b0a0", cursor: "pointer", fontSize: 16 }}
              >
                ✕
              </button>
            )}
            <Input
              placeholder="제목 (예: 처음 만난 날)"
              value={ep.title}
              onChange={(e) => updateEp(i, "title", e.target.value)}
            />
            <Textarea
              placeholder={"누가 무엇을 했는지 자유롭게 써주세요. (20자 이상)\n예: 내가 울고 있었는데 무릎에 올라와서 핥아줬다"}
              value={ep.content}
              onChange={(e) => updateEp(i, "content", e.target.value)}
            />
            <div style={{
              fontSize: 11,
              color: ep.content.length >= 20 ? "#a8b89a" : "#c8b0a0",
              textAlign: "right",
              marginTop: -8,
              marginBottom: 4,
            }}>
              {ep.content.length}자 {ep.content.length < 20 ? `(${20 - ep.content.length}자 더 써주세요)` : "✓"}
            </div>
          </div>
        ))}

        {episodes.length < 5 && (
          <button
            onClick={addEp}
            style={{
              width: "100%", padding: 14, borderRadius: 14,
              border: "1.5px dashed #e4d0bc", background: "none",
              color: "#b09070", fontSize: 16, fontWeight: 400, cursor: "pointer",
              fontFamily: "'Gowun Batang', serif", marginBottom: 12,
            }}
          >
            + 에피소드 추가하기
          </button>
        )}
      </Card>
    );

    if (step === 4) return (
      <Card>
        <StepLabel>STEP 5</StepLabel>
        <CardTitle>편지지를 골라주세요 💌</CardTitle>
        <CardDesc>{animalName || "친구"}의 편지를 담을 예쁜 편지지예요.</CardDesc>
        <PaperSelector
          selected={paper}
          onChange={setPaper}
        />
      </Card>
    );

    if (step === 5) {
      // 1) 검토 화면 (입력 내용 확인 + 수정) → 2) 완료하기 → 작성 연출 (생성 동시 진행)
      if (!reviewDone) return renderReview();

      if (phase === "form") return (
        <WritingGate
          animalName={animalName}
          ready={genReady}
          onComplete={() => setPhase("letter")}
        />
      );

      if (phase === "error") return (
        <div style={{ textAlign: "center", padding: "48px 24px", animation: "fadeUp .4s ease" }}>
          <span style={{ fontSize: 56, display: "block", marginBottom: 20 }}>😢</span>
          <div style={{ fontFamily: "'Jua', sans-serif", fontSize: 22, fontWeight: 700, color: "#5c3d2e", marginBottom: 12 }}>
            편지를 가져오는 길에 문제가 생겼어요
          </div>
          <div style={{ fontSize: 16, fontWeight: 400, color: "#b09070", lineHeight: 1.9, marginBottom: 24 }}>
            잠시 후 다시 시도해주세요.<br />
            문제가 계속되면 아래로 알려주세요.
          </div>
          <div style={{
            fontSize: 16, fontWeight: 400, color: "#c4845a", marginBottom: 24,
            background: "#fff0e8", padding: "12px 20px", borderRadius: 12,
            display: "inline-block",
          }}>
            rnak24@gmail.com
          </div>
          <div>
            <BtnMain onClick={() => {
              setReviewDone(false);
              setPhase("form");
              setStep(4);
            }}>
              다시 시도하기
            </BtnMain>
          </div>
        </div>
      );

      if (phase === "letter") return (
        <LetterResult
          letter={letter}
          userName={userName}
          animalName={animalName}
          animalType={effectiveAnimalType}
          paperId={paper}
          onGoHome={handleGoHome}
          onEditEpisodes={handleEditEpisodes}
          saveData={{
            userName, gender, animalType: effectiveAnimalType, animalName,
            aliveStatus, petAge: petAgeUnknown ? null : (petAge !== "" ? Number(petAge) : null), period,
            personalities: personalityText.trim()
              ? [...personalities, personalityText.trim()]
              : personalities,
            episodes,
          }}
        />
      );
    }
  };

  return (
    <div style={{ maxWidth: 540, margin: "0 auto", padding: "0 20px" }}>
      {/* 이어서 쓰기 모달 */}
      {showResume && (
        <ConfirmModal
          title="이어서 쓰시겠어요?"
          message={`지난번에 쓰시던 내용이 남아 있어요.\n${pendingDraft?.animalName ? `(${pendingDraft.animalName}에게 받는 편지)` : ""}`}
          confirmText="이어서 쓰기"
          cancelText="새로 시작"
          onConfirm={resumeDraft}
          onCancel={discardDraft}
        />
      )}

      {/* 뒤로가기 확인 모달 */}
      {showConfirm && (
        <ConfirmModal
          title="정말 나가시겠어요?"
          message="여기까지 쓰신 내용은 자동으로 보관돼요."
          confirmText="나가기"
          cancelText="계속 쓰기"
          onConfirm={confirmLeave}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* 뒤로가기 버튼 — 편지 결과에서는 숨김 */}
      {phase !== "letter" && (
        <button
          onClick={handleBack}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            color: "#b09070", fontSize: 16, fontWeight: 400, cursor: "pointer",
            border: "none", background: "none",
            fontFamily: "'Gowun Batang', serif",
            padding: "8px 0", margin: "24px 0 20px",
          }}
        >
          ← {step === 0 ? "처음으로" : "이전"}
        </button>
      )}

      {phase === "form" && step < 5 && (
        <ProgressBar total={TOTAL_STEPS} current={step} />
      )}

      <div key={`step-${step}-${phase}`}>
        {renderStep()}
      </div>

      {phase === "form" && step < 5 && (
        <BtnMain disabled={!canNext()} onClick={() => setStep(step + 1)}>
          다음으로 →
        </BtnMain>
      )}
    </div>
  );
}
